import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Compass, Loader2, Sparkles, Check, X, Pencil, Trash2, Bookmark, ArrowRightToLine, History,
} from "lucide-react";
import { toast } from "sonner";

const FACT_TYPES = [
  ["rent", "Rent"], ["deposit", "Deposit"], ["tenancy_dates", "Tenancy dates"],
  ["landlord_contact", "Landlord / management"], ["notice_address", "Notice address"],
  ["appliances", "Included appliances"], ["utilities", "Utilities"],
  ["parking_storage", "Parking / storage"], ["pets_guests", "Pets / guests"],
  ["key_return", "Key return"], ["renewal", "Renewal"], ["bookmark", "Bookmark"],
];
const TYPE_LABEL = Object.fromEntries(FACT_TYPES.map(([k, v]) => [k, v]));
const FIELD_LABEL = {
  deposit_amount: "Deposit amount", landlord_name: "Landlord name", landlord_email: "Landlord email",
  move_in_date: "Move-in date", move_out_date: "Move-out date", lease_end_date: "Lease end date",
};
const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");

export function LeaseNavigator({ docId, onClose }) {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [populateFor, setPopulateFor] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/vault/${docId}/lease-navigator`); setBoard(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't open the Lease Navigator."); onClose?.(); }
    finally { setLoading(false); }
  }, [docId, onClose]);

  useEffect(() => { load(); }, [load]);

  const readOnly = board?.read_only;

  const suggest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/vault/${docId}/lease-navigator/suggest`);
      await load();
      toast.success(data.count ? `${data.count} suggestions — confirm the ones you want` : "No suggestions found");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't read the document."); }
    finally { setBusy(false); }
  };

  const confirmSuggestion = async (f) => {
    setBusy(true);
    try { await api.post(`/vault/${docId}/lease-navigator/facts`, { fact_id: f.fact_id }); await load(); toast.success("Confirmed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't confirm."); }
    finally { setBusy(false); }
  };

  const dismiss = async (f) => {
    setBusy(true);
    try { await api.delete(`/vault/${docId}/lease-navigator/facts/${f.fact_id}`); await load(); }
    catch (e) { toast.error("Couldn't dismiss."); }
    finally { setBusy(false); }
  };

  const removeFact = async (f) => {
    if (!window.confirm("Remove this fact? Its history goes with it.")) return;
    setBusy(true);
    try { await api.delete(`/vault/${docId}/lease-navigator/facts/${f.fact_id}`); await load(); }
    catch (e) { toast.error("Couldn't remove."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent data-testid="lease-navigator" className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Compass size={18} className="text-brand" /> Lease Navigator</DialogTitle>
          <DialogDescription>{board?.doc?.filename ? `Facts from ${board.doc.filename}` : "Facts you frequently need"}</DialogDescription>
        </DialogHeader>

        {loading || !board ? (
          <div className="flex items-center justify-center py-10 text-[#64748B]" data-testid="ln-loading"><Loader2 className="animate-spin" size={20} /></div>
        ) : (
          <div className="space-y-4 text-sm text-[#0F172A]">
            {!readOnly && board.is_pdf && (
              <Button size="sm" variant="outline" disabled={busy} onClick={suggest} data-testid="ln-suggest-btn" className="rounded-lg h-9 text-xs w-full">
                {busy ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Sparkles size={14} className="mr-1.5" />} Suggest facts from document
              </Button>
            )}

            {/* Suggestions (drafts) */}
            {board.facts.suggested.length > 0 && (
              <div data-testid="ln-suggestions">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8]">Suggested from document — confirmation required</p>
                <div className="mt-1.5 space-y-1.5">
                  {board.facts.suggested.map((f) => (
                    <div key={f.fact_id} data-testid={`ln-suggestion-${f.fact_id}`} className="rounded-lg border border-dashed border-[#CBD5E1] bg-[#F8FAFC] p-2.5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-xs font-semibold">{TYPE_LABEL[f.fact_type] || f.fact_type}{f.label ? ` · ${f.label}` : ""}</p>
                          <p className="text-xs">{f.value}</p>
                          <p className="text-[11px] text-[#94A3B8]">Suggested from page {f.page}{f.excerpt ? ` · “${f.excerpt}”` : ""}</p>
                        </div>
                        {!readOnly && (
                          <div className="flex gap-1 shrink-0">
                            <Button size="sm" disabled={busy} onClick={() => confirmSuggestion(f)} data-testid={`ln-confirm-${f.fact_id}`} className="h-7 rounded-lg bg-brand hover:bg-brand-hover text-white text-[11px] px-2"><Check size={13} className="mr-0.5" /> Confirm</Button>
                            <Button size="sm" variant="ghost" disabled={busy} onClick={() => dismiss(f)} data-testid={`ln-dismiss-${f.fact_id}`} className="h-7 w-7 p-0 rounded-lg text-[#94A3B8]"><X size={14} /></Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Confirmed facts */}
            <div data-testid="ln-confirmed">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8]">Confirmed facts</p>
              {board.facts.confirmed.length === 0 && <p className="text-xs text-[#94A3B8] mt-1">No confirmed facts yet.</p>}
              <div className="mt-1.5 space-y-1.5">
                {board.facts.confirmed.map((f) => (
                  <FactRow key={f.fact_id} f={f} board={board} readOnly={readOnly} busy={busy}
                    onRemove={() => removeFact(f)} onPopulate={() => setPopulateFor(f)}
                    docId={docId} onChanged={load} />
                ))}
              </div>
            </div>

            {!readOnly && <BookmarkForm docId={docId} onAdded={load} />}

            <p className="text-[11px] text-[#94A3B8] bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-2" data-testid="ln-disclaimer">{board.disclaimer}</p>
          </div>
        )}

        {populateFor && (
          <PopulateDialog docId={docId} fact={populateFor} board={board}
            onClose={() => setPopulateFor(null)} onDone={() => { setPopulateFor(null); load(); }} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function FactRow({ f, board, readOnly, busy, onRemove, onPopulate, docId, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(f.value);
  const canPopulate = (board.populate_map?.[f.fact_type] || []).length > 0;

  const save = async () => {
    try { await api.patch(`/vault/${docId}/lease-navigator/facts/${f.fact_id}`, { value }); setEditing(false); onChanged(); toast.success("Corrected — earlier value kept in history"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't correct."); }
  };

  return (
    <div data-testid={`ln-fact-${f.fact_id}`} className="rounded-lg border border-[#E2E8F0] p-2.5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold flex items-center gap-1">
            {f.provenance === "user_created" && <Bookmark size={11} className="text-brand" />}
            {TYPE_LABEL[f.fact_type] || f.fact_type}{f.label ? ` · ${f.label}` : ""}
          </p>
          {editing ? (
            <div className="mt-1 flex gap-1">
              <Input data-testid={`ln-edit-input-${f.fact_id}`} value={value} onChange={(e) => setValue(e.target.value)} className="h-8 rounded-lg text-sm" />
              <Button size="sm" onClick={save} data-testid={`ln-edit-save-${f.fact_id}`} className="h-8 rounded-lg bg-brand text-white text-xs">Save</Button>
              <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setValue(f.value); }} className="h-8 rounded-lg text-xs">Cancel</Button>
            </div>
          ) : (
            <p className="text-xs">{f.value}</p>
          )}
          <p className="text-[11px] text-[#94A3B8]">
            {f.provenance === "user_created" ? "User-created bookmark · " : "Confirmed from page "}{f.provenance === "user_created" ? `page ${f.page}` : f.page}
            {f.excerpt ? ` · “${f.excerpt}”` : ""}
          </p>
          {(f.history || []).length > 0 && (
            <p className="text-[10px] text-[#94A3B8] flex items-center gap-1 mt-0.5" data-testid={`ln-fact-history-${f.fact_id}`}>
              <History size={10} /> Superseded a value confirmed {fmt(f.history[f.history.length - 1].confirmed_at)}
            </p>
          )}
          {(f.populated_to || []).length > 0 && (
            <p className="text-[10px] text-emerald-600 mt-0.5" data-testid={`ln-fact-populated-${f.fact_id}`}>Added to {FIELD_LABEL[f.populated_to[f.populated_to.length - 1].field] || f.populated_to[f.populated_to.length - 1].field}</p>
          )}
        </div>
        {!readOnly && !editing && (
          <div className="flex gap-0.5 shrink-0">
            {canPopulate && <Button size="sm" variant="ghost" onClick={onPopulate} data-testid={`ln-populate-btn-${f.fact_id}`} className="h-7 w-7 p-0 rounded-lg text-[#64748B]" title="Add to property"><ArrowRightToLine size={13} /></Button>}
            <Button size="sm" variant="ghost" onClick={() => setEditing(true)} data-testid={`ln-edit-btn-${f.fact_id}`} className="h-7 w-7 p-0 rounded-lg text-[#64748B]" title="Correct"><Pencil size={13} /></Button>
            <Button size="sm" variant="ghost" onClick={onRemove} data-testid={`ln-remove-btn-${f.fact_id}`} className="h-7 w-7 p-0 rounded-lg text-[#FCA5A5]" title="Remove"><Trash2 size={13} /></Button>
          </div>
        )}
      </div>
    </div>
  );
}

function BookmarkForm({ docId, onAdded }) {
  const [open, setOpen] = useState(false);
  const [factType, setFactType] = useState("bookmark");
  const [value, setValue] = useState("");
  const [page, setPage] = useState("1");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!value.trim()) { toast.error("Add a value or note."); return; }
    setBusy(true);
    try {
      await api.post(`/vault/${docId}/lease-navigator/facts`, { fact_type: factType, value: value.trim(), page: Number(page) || 1, bookmark: true });
      setOpen(false); setValue(""); setPage("1"); setFactType("bookmark"); onAdded(); toast.success("Bookmark added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add."); }
    finally { setBusy(false); }
  };

  if (!open) return <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="ln-bookmark-add" className="rounded-lg h-8 text-xs"><Bookmark size={13} className="mr-1" /> Add a bookmark</Button>;
  return (
    <div className="rounded-lg border border-[#E2E8F0] p-2.5 space-y-1.5" data-testid="ln-bookmark-form">
      <select data-testid="ln-bookmark-type" value={factType} onChange={(e) => setFactType(e.target.value)} className="h-9 w-full rounded-lg border border-[#E2E8F0] px-2 text-sm">
        {FACT_TYPES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      <Input data-testid="ln-bookmark-value" value={value} onChange={(e) => setValue(e.target.value)} placeholder="What you want to remember" className="h-9 rounded-lg text-sm" />
      <div className="flex items-center gap-2">
        <Label className="text-xs shrink-0">Page</Label>
        <Input data-testid="ln-bookmark-page" type="number" min="1" value={page} onChange={(e) => setPage(e.target.value)} className="h-9 w-20 rounded-lg text-sm" />
        <Button size="sm" onClick={add} disabled={busy} data-testid="ln-bookmark-save" className="h-9 rounded-lg bg-brand hover:bg-brand-hover text-white text-xs ml-auto">{busy ? <Loader2 size={13} className="animate-spin" /> : "Save"}</Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)} className="h-9 rounded-lg text-xs">Cancel</Button>
      </div>
    </div>
  );
}

function PopulateDialog({ docId, fact, board, onClose, onDone }) {
  const fields = board.populate_map?.[fact.fact_type] || [];
  const [field, setField] = useState(fields[0] || "");
  const [preview, setPreview] = useState(null);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);

  const doPreview = useCallback(async (fld) => {
    setPreview(null);
    try {
      const { data } = await api.post(`/vault/${docId}/lease-navigator/facts/${fact.fact_id}/populate`, { field: fld });
      setPreview(data); setValue(data.proposed_value ?? "");
    } catch (e) { toast.error(e?.response?.data?.detail || "Can't populate that field."); }
  }, [docId, fact.fact_id]);

  useEffect(() => { if (field) doPreview(field); }, [field, doPreview]);

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/vault/${docId}/lease-navigator/facts/${fact.fact_id}/populate`, { field, value, confirm: true });
      toast.success("Property field updated"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="ln-populate-dialog" className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Add to property</DialogTitle>
          <DialogDescription>Copy this confirmed fact into a property field. Nothing changes until you confirm.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <Label className="text-xs">Destination field</Label>
            <select data-testid="ln-populate-field" value={field} onChange={(e) => setField(e.target.value)} className="mt-1 h-10 w-full rounded-lg border border-[#E2E8F0] px-2 text-sm">
              {fields.map((f) => <option key={f} value={f}>{FIELD_LABEL[f] || f}</option>)}
            </select>
          </div>
          {preview && (
            <div className="rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] p-2.5 text-xs space-y-1" data-testid="ln-populate-preview">
              <p><span className="text-[#94A3B8]">Current value: </span>{preview.current_value ?? <em className="text-[#94A3B8]">empty</em>}</p>
              <Label className="text-[11px] text-[#94A3B8]">New value</Label>
              <Input data-testid="ln-populate-value" value={value} onChange={(e) => setValue(e.target.value)} className="h-8 rounded-lg text-sm" />
              {preview.will_overwrite && <p className="text-[11px] text-amber-600">This replaces the current value.</p>}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-lg" data-testid="ln-populate-cancel">Cancel</Button>
          <Button onClick={confirm} disabled={busy || !field} data-testid="ln-populate-confirm" className="rounded-lg bg-brand hover:bg-brand-hover text-white">{busy ? <Loader2 size={15} className="animate-spin" /> : "Confirm & add"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
